// Panel único del intento de MetaMap del paso 02 (identidad, selfie y comprobante): stepper, QR o
// SDK, una instrucción corta por etapa y las salidas para cambiar de método o relanzar.
import type { ReactNode } from 'react';
import { AlertTriangle, Check, Clock, Smartphone, Tablet, TabletSmartphone } from 'lucide-react';
import { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import { Button, ConfirmDialog, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { LAUNCH_ACTION, type LaunchAction, type MetamapAttempt } from '../api/useMetamapAttempt';
import { useElapsedSince } from '../api/useElapsedSince';
import { ATTEMPT_STEPS, attemptHeadline, attemptStepStatuses } from '../metamapAttempt';
import type { Step02RequestCopy } from '../step02Request';
import type { KycModality } from '../types';
import { CopyLinkButton, LinkQrCode } from './CustomerLinkQr';
import { MetamapWidget } from './MetamapWidget';
import { StateIcon, WaitingIcon } from './KycScreenBits';

const S = KycAttemptStageEnum;
const SDK_DESCRIPTION =
    'Entrega la tablet al cliente. Al terminar, el resultado aparece solo en esta pantalla.';

function otherModality(modality: KycModality): KycModality {
    return modality === 'CUSTOMER_LINK' ? 'STORE_DEVICE' : 'CUSTOMER_LINK';
}

/** Texto del botón mientras su prepare vuela. */
function busyLabel(modality: KycModality): string {
    return modality === 'CUSTOMER_LINK' ? 'Generando QR…' : 'Abriendo verificación…';
}

export interface MetamapAttemptPanelProps {
    attempt: MetamapAttempt;
    copy: Step02RequestCopy;
    /** Debajo de la etapa: avisos de error y las estaciones del KYC. */
    children?: ReactNode;
}

/** El modal del SDK y la confirmación los monta la pantalla, fuera del cambio de estado. */
export function MetamapAttemptPanel({ attempt, copy, children }: MetamapAttemptPanelProps) {
    const resolved = attempt.stage === S.RESULT;
    return (
        <>
            {resolved ? null : attempt.timedOut ? (
                <TimedOutBody attempt={attempt} />
            ) : attempt.stage === S.EXPIRED ? (
                <ExpiredBody attempt={attempt} />
            ) : (
                <InFlightBody attempt={attempt} copy={copy} />
            )}
            {resolved ? null : <AttemptNotices attempt={attempt} copy={copy} />}
            {children}
        </>
    );
}

/** Los 4 pasos de la tarea; RESULT y EXPIRED tienen pantalla propia. */
export function AttemptStepper({ stage }: { stage: KycAttemptStageEnum }) {
    const statuses = attemptStepStatuses(stage);
    if (!statuses) return null;
    return (
        <ol
            aria-label="Avance de la verificación"
            className="mx-auto mb-5 grid max-w-md grid-cols-4 gap-2"
        >
            {ATTEMPT_STEPS.map((label, index) => {
                const status = statuses[index];
                return (
                    <li
                        key={label}
                        data-status={status}
                        aria-current={status === 'current' ? 'step' : undefined}
                        className="flex flex-col items-center gap-1 text-center"
                    >
                        <span
                            className={cn(
                                'flex size-6 items-center justify-center rounded-full border text-[11px] font-semibold',
                                status === 'done' && 'border-success bg-success-soft text-success',
                                status === 'current' &&
                                    'border-primary bg-primary text-primary-foreground',
                                status === 'pending' && 'border-border text-muted-foreground',
                            )}
                        >
                            {status === 'done' ? (
                                <Check className="size-3.5" aria-hidden />
                            ) : (
                                index + 1
                            )}
                        </span>
                        <span
                            className={cn(
                                'text-[11px] leading-tight',
                                status === 'pending' ? 'text-muted-foreground' : 'font-medium',
                            )}
                        >
                            {label}
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}

/** Modal del SDK: solo se cierra con su botón; cerrarlo no cancela la verificación.
 * No modal: el SDK monta su iframe en el body y un diálogo modal le bloquearía los clics. */
export function AttemptSdkModal({ attempt, title }: { attempt: MetamapAttempt; title: string }) {
    const sdk = attempt.sdk;
    return (
        <Modal
            isOpen={sdk !== null}
            onClose={attempt.closeSdk}
            dismissible={false}
            modal={false}
            title={title}
            description={SDK_DESCRIPTION}
            size="md"
        >
            {sdk && (
                <MetamapWidget
                    key={sdk.attemptId ?? sdk.config.metadata}
                    clientId={sdk.config.clientId}
                    flowId={sdk.config.flowId}
                    metadata={sdk.config.metadata}
                    onStarted={attempt.sdkCallbacks.onStarted}
                    onFinished={attempt.sdkCallbacks.onFinished}
                    onExited={attempt.sdkCallbacks.onExited}
                />
            )}
        </Modal>
    );
}

/** Confirmación al cambiar de método o relanzar con el cliente ya empezado. */
export function SwitchConfirmDialog({ attempt }: { attempt: MetamapAttempt }) {
    return (
        <ConfirmDialog
            open={attempt.pendingSwitch !== null}
            onOpenChange={(open) => {
                if (!open) attempt.cancelSwitch();
            }}
            title="¿Descartar la verificación en curso?"
            description="Se descartará la verificación que está en curso. El cliente tendrá que empezar de nuevo."
            confirmLabel="Sí, descartar"
            cancelLabel="Seguir esperando"
            destructive
            loading={attempt.isPending}
            onConfirm={attempt.confirmSwitch}
        />
    );
}

/** QR sin liga o tablet sin config: se dice, con la otra vía a la mano. */
export function AttemptNotices({
    attempt,
    copy,
}: {
    attempt: MetamapAttempt;
    copy: Step02RequestCopy;
}) {
    return (
        <>
            {attempt.linkUnavailable && (
                <Notice title="La verificación en el celular del cliente aún no está disponible">
                    Usa la opción <span className="font-medium">En esta tablet</span>.{' '}
                    {copy.tabletHint}.
                </Notice>
            )}
            {attempt.configUnavailable && <Notice title={copy.configUnavailable} />}
        </>
    );
}

function Notice({ title, children }: { title: string; children?: ReactNode }) {
    return (
        <div
            role="status"
            className="mx-auto mb-5 flex max-w-md items-start gap-2 rounded-lg border border-warning-soft-border bg-warning-soft p-4 text-left"
        >
            <AlertTriangle className="mt-0.5 size-4 flex-none text-warning" aria-hidden />
            <div className="text-[12px] leading-relaxed text-warning">
                <p className="font-medium">{title}</p>
                {children ? <p>{children}</p> : null}
            </div>
        </div>
    );
}

/** Título y subtexto de la etapa, anunciados juntos. */
function Headline({ title, sub }: { title: string; sub: string }) {
    return (
        <div role="status" aria-live="polite" className="mx-auto mb-5 max-w-md">
            <p className="mb-1 text-[15px] font-semibold">{title}</p>
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">{sub}</p>
        </div>
    );
}

/** Desde cuándo cuenta el «Empezó hace…»: el `stageAt` de STARTED del silo. */
function startedAt(attempt: MetamapAttempt): string | null {
    return attempt.stage === S.STARTED && attempt.progress?.stage === S.STARTED
        ? attempt.progress.stageAt
        : null;
}

function InFlightBody({ attempt, copy }: { attempt: MetamapAttempt; copy: Step02RequestCopy }) {
    const { stage, progress } = attempt;
    const msSinceStart = useElapsedSince(startedAt(attempt));
    const headline = attemptHeadline(stage, progress?.modality ?? null, copy, {
        qrShown: attempt.qrUrl !== null,
        msSinceStart,
    });
    return (
        <>
            {stage !== null && <AttemptStepper stage={stage} />}
            <StageVisual attempt={attempt} qrTitle={copy.qrTitle} />
            <Headline title={headline.title} sub={headline.sub} />
            {attempt.clientLeft && (
                <Notice title="El cliente salió de la verificación">
                    Puedes volver a lanzarla o cambiar de método.
                </Notice>
            )}
            {attempt.stuck && (
                <Notice title="¿El cliente se atoró?">
                    Lleva más de 5 minutos sin avanzar. Puedes volver a lanzarla o cambiar de
                    método.
                </Notice>
            )}
            <InFlightActions attempt={attempt} />
        </>
    );
}

/** El QR centrado mientras toca escanearlo; si no, un ícono de qué está pasando. */
function StageVisual({ attempt, qrTitle }: { attempt: MetamapAttempt; qrTitle: string }) {
    if (attempt.qrUrl) {
        return (
            <div className="mx-auto mb-4 w-fit max-w-full">
                <LinkQrCode url={attempt.qrUrl} title={qrTitle} />
            </div>
        );
    }
    const modality = attempt.progress?.modality;
    if (attempt.stage === S.STARTED) {
        return (
            <StateIcon tone="info">
                {modality === 'CUSTOMER_LINK' ? (
                    <Smartphone className="size-7" aria-hidden />
                ) : (
                    <Tablet className="size-7" aria-hidden />
                )}
            </StateIcon>
        );
    }
    if (attempt.stage === S.AWAITING_START && modality === 'STORE_DEVICE') {
        return (
            <StateIcon tone="info">
                <TabletSmartphone className="size-7" aria-hidden />
            </StateIcon>
        );
    }
    return <WaitingIcon />;
}

/** Botón que lanza un prepare: solo el tocado carga; los demás se apagan sin girar. */
function LaunchButton({
    attempt,
    modality,
    action,
    variant = 'secondary',
    children,
}: {
    attempt: MetamapAttempt;
    modality: KycModality;
    action: LaunchAction;
    variant?: 'default' | 'secondary' | 'ghost';
    children: string;
}) {
    const loading = attempt.busyAction === action;
    return (
        <Button
            variant={variant}
            size="sm"
            isLoading={loading}
            disabled={attempt.isPending && !loading}
            onClick={() => {
                attempt.launch(modality, action);
            }}
        >
            {loading ? busyLabel(modality) : children}
        </Button>
    );
}

function ActionRow({ children }: { children: ReactNode }) {
    return <div className="mb-5 flex flex-wrap justify-center gap-2">{children}</div>;
}

function InFlightActions({ attempt }: { attempt: MetamapAttempt }) {
    const { progress, stage } = attempt;
    // Sin intento (sesión previa al despliegue) se ofrecen las dos vías desde cero.
    if (progress === null)
        return (
            <RelaunchBoth
                attempt={attempt}
                qrLabel="Mostrar el QR"
                tabletLabel="Usar esta tablet"
            />
        );
    const modality = progress.modality;
    if (modality === 'STORE_DEVICE' && stage === S.AWAITING_START && !attempt.clientLeft) {
        return <TabletHandoffActions attempt={attempt} />;
    }
    const onPhone = modality === 'CUSTOMER_LINK';
    return (
        <ActionRow>
            {attempt.qrUrl && <CopyLinkButton url={attempt.qrUrl} disabled={attempt.isPending} />}
            {attempt.qrHidden && (
                <Button
                    variant="secondary"
                    size="sm"
                    disabled={attempt.isPending}
                    onClick={attempt.revealQr}
                >
                    Mostrar el QR
                </Button>
            )}
            <LaunchButton
                attempt={attempt}
                modality={otherModality(modality)}
                action={LAUNCH_ACTION.SWITCH}
            >
                {onPhone
                    ? stage === S.AWAITING_START
                        ? 'Usar esta tablet'
                        : 'Cambiar a esta tablet'
                    : 'Cambiar a QR'}
            </LaunchButton>
            <LaunchButton
                attempt={attempt}
                modality={modality}
                action={LAUNCH_ACTION.RELAUNCH}
                variant="ghost"
            >
                {onPhone ? 'Generar otro QR' : 'Volver a lanzar'}
            </LaunchButton>
        </ActionRow>
    );
}

/** Tablet antes de que el cliente empiece: abrir el SDK o irse al QR. */
function TabletHandoffActions({ attempt }: { attempt: MetamapAttempt }) {
    const opening = attempt.busyAction === LAUNCH_ACTION.REOPEN;
    return (
        <ActionRow>
            <Button
                size="sm"
                isLoading={opening}
                disabled={attempt.sdk !== null || (attempt.isPending && !opening)}
                onClick={attempt.reopenSdk}
            >
                {opening ? 'Abriendo verificación…' : 'Empezar verificación'}
            </Button>
            <LaunchButton attempt={attempt} modality="CUSTOMER_LINK" action={LAUNCH_ACTION.SWITCH}>
                Mejor por QR
            </LaunchButton>
        </ActionRow>
    );
}

function RelaunchBoth({
    attempt,
    qrLabel = 'Relanzar por QR',
    tabletLabel = 'Relanzar en esta tablet',
}: {
    attempt: MetamapAttempt;
    qrLabel?: string;
    tabletLabel?: string;
}) {
    return (
        <ActionRow>
            <LaunchButton attempt={attempt} modality="CUSTOMER_LINK" action={LAUNCH_ACTION.QR}>
                {qrLabel}
            </LaunchButton>
            <LaunchButton attempt={attempt} modality="STORE_DEVICE" action={LAUNCH_ACTION.TABLET}>
                {tabletLabel}
            </LaunchButton>
        </ActionRow>
    );
}

function ExpiredBody({ attempt }: { attempt: MetamapAttempt }) {
    return (
        <>
            <StateIcon tone="warning">
                <Clock className="size-7" aria-hidden />
            </StateIcon>
            <Headline
                title="La verificación venció"
                sub="El cliente no la terminó a tiempo. Vuelve a lanzarla por QR o en esta tablet."
            />
            <RelaunchBoth attempt={attempt} />
        </>
    );
}

function TimedOutBody({ attempt }: { attempt: MetamapAttempt }) {
    return (
        <>
            <StateIcon tone="warning">
                <AlertTriangle className="size-7" aria-hidden />
            </StateIcon>
            <Headline
                title="Esto ya tardó demasiado"
                sub="No avanzó en 25 minutos. Consulta ahora, vuelve a lanzarla o cambia de método."
            />
            <div className="mb-3">
                <Button
                    variant="outline"
                    disabled={attempt.isPending}
                    onClick={() => {
                        void attempt.checkNow();
                    }}
                >
                    Consultar ahora
                </Button>
            </div>
            <RelaunchBoth attempt={attempt} />
        </>
    );
}
